import React, { FC, useEffect, useRef } from "react";
import {
  ExternalChatChannel,
  ExternalChatOrganization,
  ExternalChatUserState,
  ExternalChatUserStatus,
} from "domain-entities";
import { Box, IconButton, List, ListItem, makeStyles, } from "@material-ui/core";
import ErrorView from "Components/Layout/ErrorView";
import { color, px } from "Configs/theme";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useTranslation } from "Providers/TranslationProvider";
import { Skeleton } from "@material-ui/lab";
import FadeInBox from "Components/Layout/FadeInBox";
import { CollectionStore, DocumentsOnceStore } from "Helpers/StoreHelper";
import ChannelTypeView from "./ChannelTypeView";
import OrganizationAvatar from "Components/Organization/OrganizationAvatar";

interface Props {
  organization: ExternalChatOrganization;
  viewerUserState: ExternalChatUserState;
  selectedChannelId: string | undefined;
  channelsStore: CollectionStore<ExternalChatChannel>;
  userStatusesOnceStore: DocumentsOnceStore<ExternalChatUserStatus>;
  setSelectedChannel: (channel: ExternalChatChannel) => void;
  onClose: () => void;
}

const ChannelsView: FC<Props> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const hasAutoSelectedRef = useRef(false);
  const firstChannel = props.channelsStore.items[0];
  const setSelectedChannel = props.setSelectedChannel;

  useEffect(() => {
    if (
      !props.channelsStore.isLoading &&
      !hasAutoSelectedRef.current &&
      firstChannel
    ) {
      setSelectedChannel(firstChannel);
      hasAutoSelectedRef.current = true;
    }
  }, [props.channelsStore.isLoading, firstChannel, setSelectedChannel]);

  function renderUnreadIndicator(channel: ExternalChatChannel) {
    const isChannelUnread = props.viewerUserState.unreadChannels.includes(
      `${props.organization.id}/${channel.id}`
    );

    if (isChannelUnread) {
      return <Box className={classes.unreadIndicator} />;
    }
  }

  function renderChannels() {
    if (props.channelsStore.error) {
      return <ErrorView error={props.channelsStore.error} />;
    } else if (props.channelsStore.isLoading) {
      return [1, 2, 3].map((index) => (
        <FadeInBox key={index} delayIndex={index} className={classes.listItem}>
          <Skeleton variant="circle" width={AVATAR_SIZE} height={AVATAR_SIZE} />
          <Box className={classes.content}>
            <Box className={classes.name}>
              <Skeleton height={14} width="50%" />
            </Box>
            <Box className={classes.type}>
              <Skeleton height={12} width="20%" />
            </Box>
          </Box>
        </FadeInBox>
      ));
    } else if (props.channelsStore.items.length) {
      return props.channelsStore.items.map((channel, index) => {
        let classNames = `${classes.listItem}`;

        if (props.selectedChannelId === channel.id) {
          classNames += ` active`
        }

        return (
            <FadeInBox key={channel.id} delayIndex={index}>
              <ListItem
                  className={classNames}
                  onClick={() => props.setSelectedChannel(channel)}
                  button
              >
                <Box className={classes.avatarContainer}>
                  <OrganizationAvatar organization={props.organization} isCirlce/>
                </Box>
                <Box className={classes.content}>
                  <Box className={classes.contentFirstRow}>
                    <Box className={classes.name}>{channel.name}</Box>
                    {renderUnreadIndicator(channel)}
                  </Box>
                  <Box className={classes.type}>
                    {[...channel.usersActive, ...channel.usersFormer].length > 2
                        ? t("ChannelsView_ChannelType_Group")
                        : t("ChannelsView_ChannelType_Direct")}
                  </Box>
                  <Box className={classes.status}>
                    <ChannelTypeView
                        channel={channel}
                        userStatusesOnceStore={props.userStatusesOnceStore}
                    />
                  </Box>
                </Box>
              </ListItem>
            </FadeInBox>
        )
      });
    } else {
      return (
          <ErrorView
              severity="info"
              color="warning"
              padding={px.none}
              error={new Error(t("ChannelsView_Error_NoChannels"))}
          />
      );
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <IconButton onClick={props.onClose} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box className={classes.outerChannelName}>
          <Box className={classes.innerChannelName}>
            {props.organization.name}
          </Box>
        </Box>
      </Box>
      <List className={classes.list}>{renderChannels()}</List>
    </Box>
  );
};

export default ChannelsView;

const AVATAR_SIZE = 48;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  header: {
    display: "flex",
    marginBottom: px.s,
  },
  outerChannelName: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    width: 0,
    margin: `0 ${px.s}`,
  },
  innerChannelName: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: "bold",
  },
  list: {
    padding: px.none,
  },
  listItem: {
    display: "flex",
    backgroundColor: color.card,
    borderRadius: px.xs,
    marginBottom: px.s,
    padding: px.m,
  },
  avatarContainer: {
    position: "relative",
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flex: 1,
    paddingLeft: px.m,
    width: 0,
  },
  contentFirstRow: {
    display: "flex",
  },
  unreadIndicator: {
    backgroundColor: color.primary,
    marginLeft: px.s,
    borderRadius: px.m,
    minWidth: px.m,
    height: px.m,
  },
  name: {
    fontSize: "14px",
    fontWeight: "bold",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    width: "100%",
  },
  type: {
    fontSize: "12px",
    fontWeight: "bold",
    textTransform: "capitalize",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    opacity: 0.5,
    marginBottom: px.s,
  },
  status: {
    fontWeight: "bold",
  }
});
